import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { Empty, message, Pagination, Table, Tooltip, Tag } from 'antd';
import { MakeApiCall } from '../../../../../apis';
import { ConvertParams, defaultPageSize } from '../../../../../config';
import { fakeProImage } from '../../../../../config/fakeData';
import ProductDetailsModal from "../../../../../components/modal";
import { getAmount } from "../../../../../utils/helper";
import { buildCacheKey, getProductCache, setProductCache } from '../../../../../utils/Productcache';

// ─── Constants ────────────────────────────────────────────────────────────────

const AMAZON_BASE_URL = "https://www.amazon.in/dp/";

/**
 * Static definition of every filterable column.
 * Consumed by FilterPanel (in ItemMaster) to build the column selector.
 * type: 'numeric'  → operators:  >=  <=  =
 * type: 'text'     → operators:  contains  = (exact)
 */
export const FILTERABLE_COLUMNS = [
    { key: 'productSearch',    label: 'Product (SKU / ASIN / Title)', type: 'text'    },
    { key: 'tags',             label: 'Tags',                         type: 'text'    },
    { key: 'total_units',      label: 'Units Sold',                   type: 'numeric' },
    { key: 'refund_unit',      label: 'Refund Units',                 type: 'numeric' },
    { key: 'refund_cost',      label: 'Refund Cost',                  type: 'numeric' },
    { key: 'sales',            label: 'Sales',                        type: 'numeric' },
    { key: 'ad_sales',         label: 'Ad Sales',                     type: 'numeric' },
    { key: 'ad_spend',         label: 'Ad Spend',                     type: 'numeric' },
    { key: 'cogs',             label: 'COGS',                         type: 'numeric' },
    { key: 'organic_sales',    label: 'Organic Sales',                type: 'numeric' },
    { key: 'tax',              label: 'Tax',                          type: 'numeric' },
    { key: 'fees',             label: 'Fees',                         type: 'numeric' },
    { key: 'gross_profit',     label: 'Gross Profit',                 type: 'numeric' },
    { key: 'net_profit',       label: 'Net Profit',                   type: 'numeric' },
    { key: 'inventory',        label: 'Inventory',                    type: 'numeric' },
    { key: 'days_on_hand',     label: 'Days Until Stockout',          type: 'numeric' },
    { key: 'inventory_status', label: 'Inventory Health',             type: 'text'    },
    { key: 'status',           label: 'Status',                       type: 'text'    },
];

// ─── Pure helpers (outside component — no closure over state) ────────────────

/**
 * Resolve the value to filter against for a given column key.
 * Handles: virtual keys (productSearch), nested fields (tax), array fields (tags),
 * and the status 0/1 → readable string mapping.
 */
const getFilterableValue = (row, key) => {
    switch (key) {
        case 'productSearch':
            return `${row.sku ?? ''} ${row.asin ?? ''} ${row.product_title ?? ''}`;
        case 'tags':
            return Array.isArray(row.tags) ? row.tags.join(' ') : '';
        case 'tax':
            return row?.more_data?.tax_liability?.total ?? 0;
        case 'status':
            return row.status === 0 ? 'Active' : 'Inactive';
        default:
            return row[key] ?? '';
    }
};

/**
 * Apply an array of operator-based filter objects to a dataset.
 *
 * Each filter object: { id, column, type, operator, value }
 *   Numeric operators : 'gte' (>=)  |  'lte' (<=)  |  'eq' (=)
 *   Text operators    : 'contains'  |  'eq' (exact match)
 *
 * Pure function — never mutates the input array.
 */
const applyFilters = (data, activeFilters) => {
    if (!activeFilters.length) return data;

    return data.filter(row =>
        activeFilters.every(f => {
            const cellValue = getFilterableValue(row, f.column);

            if (f.type === 'numeric') {
                const n         = parseFloat(cellValue);
                const threshold = parseFloat(f.value);
                if (isNaN(threshold)) return true;
                if (f.operator === 'gte') return n >= threshold;
                if (f.operator === 'lte') return n <= threshold;
                if (f.operator === 'eq')  return n === threshold;
            } else {
                const s = String(cellValue).toLowerCase();
                const q = String(f.value).toLowerCase();
                if (f.operator === 'contains') return s.includes(q);
                if (f.operator === 'eq')       return s === q;
            }
            return true;
        })
    );
};

// ─── Products component ───────────────────────────────────────────────────────

const Products = ({
                      userData,
                      selectedMarketplace,
                      selectedCurrencySign,
                      selectedTileData,
                      onColumnsReady,
                      onFiltersReady,   // ← new: surfaces filter controls to ItemMaster
                  }) => {

    // ── Data ─────────────────────────────────────────────────────────────────
    const [loading, setLoading]                   = useState(false);
    const [allProducts, setAllProducts]           = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [totals, setTotals]                     = useState(null);

    // ── UI ───────────────────────────────────────────────────────────────────
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const [isMobile, setIsMobile]       = useState(window.innerWidth < 768);

    // ── Filter state (array of operator-based filter objects) ────────────────
    const [activeFilters, setActiveFilters] = useState([]);

    // ── Client-side pagination ───────────────────────────────────────────────
    const [page, setPage]         = useState(1);
    const [pageSize, setPageSize] = useState(defaultPageSize);

    // ── API params (no pagination — always fetches full dataset) ─────────────
    const [apiFilter, setApiFilter] = useState({
        marketplace_id: selectedMarketplace?.id,
        start_date:     selectedTileData?.start_date,
        end_date:       selectedTileData?.end_date,
        sku:            selectedTileData?.sku,
    });

    const isFirstRender = useRef(true);

    // ─── Resize listener ─────────────────────────────────────────────────────
    const handleResize = useCallback(() => setIsMobile(window.innerWidth < 768), []);
    useEffect(() => {
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [handleResize]);

    // ─── Modal ────────────────────────────────────────────────────────────────
    const openDetails = useCallback(record => {
        setSelectedRow(record);
        setIsModalOpen(true);
    }, []);

    // ─── Filter mutation handlers (stable refs) ───────────────────────────────
    const addFilter = useCallback(filterObj => {
        setActiveFilters(prev => [...prev, filterObj]);
        setPage(1);
    }, []);

    const removeFilter = useCallback(id => {
        setActiveFilters(prev => prev.filter(f => f.id !== id));
        setPage(1);
    }, []);

    /** Replace an existing filter in-place (preserves its id & position). */
    const updateFilter = useCallback((id, updatedFields) => {
        setActiveFilters(prev =>
            prev.map(f => f.id === id ? { ...f, ...updatedFields } : f)
        );
        setPage(1);
    }, []);

    const clearFilters = useCallback(() => {
        setActiveFilters([]);
        setPage(1);
    }, []);

    // ─── Re-apply filters whenever data or filters change ────────────────────
    useEffect(() => {
        setFilteredProducts(applyFilters(allProducts, activeFilters));
        setPage(1);
    }, [activeFilters, allProducts]);

    // ─── Surface filter controls to ItemMaster (header filter panel) ─────────
    useEffect(() => {
        if (typeof onFiltersReady !== 'function') return;
        onFiltersReady({
            filterableColumns: FILTERABLE_COLUMNS,
            activeFilters,
            addFilter,
            updateFilter,
            removeFilter,
            clearFilters,
        });
    }, [activeFilters, addFilter, updateFilter, removeFilter, clearFilters, onFiltersReady]);

    // ─── Data fetching (localStorage-first) ──────────────────────────────────
    const fetchProducts = useCallback(async currentFilter => {
        if (!currentFilter?.marketplace_id) {
            setAllProducts([]);
            return;
        }

        const { start_date, end_date } = currentFilter;
        const cacheKey = buildCacheKey(start_date, end_date);

        // 1. Cache hit — use stored records, still refresh totals
        const cached = getProductCache(cacheKey);
        if (cached) {
            setAllProducts(cached);
            try {
                const totalRes = await MakeApiCall(
                    `dashboard/tiles/get-product-analytics-data-total${ConvertParams(currentFilter)}`,
                    "get", null, true, {}, userData?.token
                );
                if (totalRes?.status) setTotals(totalRes.data);
            } catch { /* non-critical */ }
            return;
        }

        // 2. Cache miss — fetch full list + totals in parallel
        setLoading(true);
        try {
            const [dataRes, totalRes] = await Promise.all([
                MakeApiCall(
                    `dashboard/tiles/get-product-analytics-data${ConvertParams({
                        ...currentFilter,
                        no_pagination: true,
                    })}`,
                    "get", null, true, {}, userData?.token
                ),
                MakeApiCall(
                    `dashboard/tiles/get-product-analytics-data-total${ConvertParams(currentFilter)}`,
                    "get", null, true, {}, userData?.token
                ),
            ]);

            if (dataRes?.status) {
                const raw     = dataRes.data?.records;
                const records = Array.isArray(raw)
                    ? raw
                    : Array.isArray(raw?.summaryData)
                        ? raw.summaryData
                        : [];
                setProductCache(cacheKey, records);
                setAllProducts(records);
            } else {
                message.warning(dataRes?.message || 'Failed to load product data');
            }

            if (totalRes?.status) setTotals(totalRes.data);

        } catch {
            message.error("Failed to load product data");
        } finally {
            setLoading(false);
        }
    }, [userData?.token]);

    useEffect(() => {
        fetchProducts(apiFilter);
    }, [apiFilter, fetchProducts]);

    // Sync apiFilter (+ reset filters) when marketplace / tile selection changes
    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setActiveFilters([]);
        setPage(1);
        setApiFilter({
            marketplace_id: selectedMarketplace?.id,
            start_date:     selectedTileData?.start_date,
            end_date:       selectedTileData?.end_date,
            sku:            selectedTileData?.sku,
        });
    }, [
        selectedMarketplace?.id,
        selectedTileData?.start_date,
        selectedTileData?.end_date,
        selectedTileData?.sku,
    ]);

    // ─── Paged slice ─────────────────────────────────────────────────────────
    const pagedProducts = useMemo(() => {
        const start = (page - 1) * pageSize;
        return filteredProducts.slice(start, start + pageSize);
    }, [filteredProducts, page, pageSize]);

    // ─── Column definitions (plain string titles — no inline filter inputs) ──
    const allColumns = useMemo(() => [
        {
            title: "#",
            key: "index",
            width: 60,
            render: (_, __, index) => (page - 1) * pageSize + index + 1,
        },
        {
            title: "Product Details",
            key: "productDetails",
            fixed: 'left',
            width: 450,
            render: (_, record) => {
                const productUrl = record?.asin ? `${AMAZON_BASE_URL}${record.asin}` : "#";
                return (
                    <div className="d-flex align-items-center bdr-left-tbl bg-gray-200 rounded-4 p-3 py-2">
                        <a href={productUrl} target="_blank" rel="noopener noreferrer"
                           className="symbol symbol-65px overflow-hidden me-3 border border-gray-300 rounded-2 shadow-sm">
                            <img src={record?.image_url || fakeProImage} alt="product" style={{ objectFit: 'contain' }} />
                        </a>
                        <div className="d-flex flex-column">
                            <a className="text-gray-800 fs-6 text-hover-danger mb-1 fw-bolder text-one min-w-250px"
                               href={productUrl} target="_blank" rel="noopener noreferrer">
                                {record?.product_title || ''}
                            </a>
                            <div className="fs-7 mt-1">
                                <span><b>SKU:</b> {record?.sku}</span>
                                {' / '}
                                <span><b>ASIN:</b> {record?.asin}</span>
                            </div>
                            <div className="fs-7 mt-1">
                                <span><b>{getAmount(record?.price, selectedCurrencySign)}</b></span>
                                {' / '}
                                <span>{record?.fulfillment_channel}</span>
                                {' / '}
                                <span>COGS {getAmount(record?.item_cogs, selectedCurrencySign)}</span>
                            </div>
                        </div>
                    </div>
                );
            },
        },
        {
            title: "Tags",
            key: "tags",
            dataIndex: "tags",
            width: 160,
            render: (tags) => {
                if (!tags?.length) return <span className="text-muted fs-8">—</span>;
                return (
                    <div className="d-flex flex-wrap gap-1">
                        {tags.map((tag, idx) => (
                            <Tag key={idx}
                                 style={{ color: '#888', borderColor: '#d9d9d9', background: '#f5f5f5', fontSize: '11px' }}>
                                {tag}
                            </Tag>
                        ))}
                    </div>
                );
            },
        },
        {
            title: "Units Sold",
            key: "total_units",
            dataIndex: "total_units",
            sorter: (a, b) => a.total_units - b.total_units,
        },
        {
            title: "Refund Units",
            key: "refund_unit",
            dataIndex: "refund_unit",
            sorter: (a, b) => a.refund_unit - b.refund_unit,
            render: (val, record) => {
                const reasons = record?.refund_breakup?.reasons || [];
                const tooltipContent = (
                    <div className="p-2" style={{ minWidth: '200px' }}>
                        {reasons.length > 0
                            ? reasons.map((item, idx) => (
                                <div key={idx} className="d-flex justify-content-between gap-3 mb-1">
                                    <span className="text-capitalize">
                                        {item.reason.toLowerCase().replace(/_/g, ' ')}:
                                    </span>
                                    <b>{item.count}</b>
                                </div>
                            ))
                            : <div className="text-muted">No specific reasons provided</div>}
                    </div>
                );
                return (
                    <Tooltip title={tooltipContent} placement="top" color="#1e1e2d">
                        <span className="fw-bold cursor-pointer text-danger"
                              style={{ borderBottom: "1px dotted #ff4d4f" }}>
                            {val}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            title: "Refund Cost",
            key: "refund_cost",
            dataIndex: "refund_cost",
            sorter: (a, b) => a.refund_cost - b.refund_cost,
            render: (val, record) => {
                const breakdown = record?.more_data?.refund_cost || {};
                const tooltipContent = (
                    <div className="p-2" style={{ minWidth: '220px' }}>
                        <div className="d-flex justify-content-between gap-3 mb-1">
                            <span>Total:</span>
                            <b>{getAmount(breakdown.total ?? 0, selectedCurrencySign)}</b>
                        </div>
                        <div className="d-flex justify-content-between gap-3 mb-1">
                            <span>Refunded Amount:</span>
                            <b>{getAmount(breakdown.refunded_amount ?? 0, selectedCurrencySign)}</b>
                        </div>
                        <div className="d-flex justify-content-between gap-3 mb-1">
                            <span>Fixed Closing Fee:</span>
                            <b>{getAmount(breakdown.fixed_closing_fee ?? 0, selectedCurrencySign)}</b>
                        </div>
                        <div className="d-flex justify-content-between gap-3">
                            <span>Ship Promotion:</span>
                            <b>{getAmount(breakdown.ship_promotion ?? 0, selectedCurrencySign)}</b>
                        </div>
                    </div>
                );
                return (
                    <Tooltip title={tooltipContent} placement="top" color="#1e1e2d">
                        <span className="fw-bold cursor-pointer text-danger"
                              style={{ borderBottom: "1px dotted #ff4d4f" }}>
                            {getAmount(val, selectedCurrencySign)}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            title: "Sales",
            key: "sales",
            dataIndex: "sales",
            sorter: (a, b) => a.sales - b.sales,
            render: (val) => <b className="text-gray-800">{getAmount(val, selectedCurrencySign)}</b>,
        },
        {
            title: "Ad Sales",
            key: "ad_sales",
            dataIndex: "ad_sales",
            sorter: (a, b) => a.ad_sales - b.ad_sales,
            render: (val) => getAmount(val, selectedCurrencySign),
        },
        {
            title: "Ad Spend",
            key: "ad_spend",
            dataIndex: "ad_spend",
            sorter: (a, b) => a.ad_spend - b.ad_spend,
            render: (val) => getAmount(val, selectedCurrencySign),
        },
        {
            title: "COGS",
            key: "cogs",
            dataIndex: "cogs",
            sorter: (a, b) => a.cogs - b.cogs,
            render: (val) => getAmount(val, selectedCurrencySign),
        },
        {
            title: "Organic Sales",
            key: "organic_sales",
            dataIndex: "organic_sales",
            sorter: (a, b) => a.organic_sales - b.organic_sales,
            render: (val) => getAmount(val, selectedCurrencySign),
        },
        {
            title: "Tax",
            key: "tax",
            dataIndex: ['more_data', 'tax_liability', 'total'],
            sorter: (a, b) =>
                (a?.more_data?.tax_liability?.total || 0) - (b?.more_data?.tax_liability?.total || 0),
            render: (val) => getAmount(val || 0, selectedCurrencySign),
        },
        {
            title: "Fees",
            key: "fees",
            dataIndex: "fees",
            sorter: (a, b) => a.fees - b.fees,
            render: (val) => getAmount(val, selectedCurrencySign),
        },
        {
            title: "Gross Profit",
            key: "gross_profit",
            dataIndex: "gross_profit",
            sorter: (a, b) => a.gross_profit - b.gross_profit,
            render: (val) => getAmount(val, selectedCurrencySign),
        },
        {
            title: "Net Profit",
            key: "net_profit",
            dataIndex: "net_profit",
            sorter: (a, b) => a.net_profit - b.net_profit,
            render: (val) => (
                <span className="fw-bold text-success">{getAmount(val, selectedCurrencySign)}</span>
            ),
        },
        {
            title: "Inventory",
            key: "inventory",
            dataIndex: "inventory",
            sorter: (a, b) => a.inventory - b.inventory,
            render: (val, record) => {
                const inv = record?.more_data?.total_inventories || {};
                const tooltipContent = (
                    <div className="p-2" style={{ minWidth: '180px' }}>
                        <div>FBA Available: <b>{inv.fba_available_inventory ?? 0}</b></div>
                        <div>Reserved: <b>{inv.reserved_inventory ?? 0}</b></div>
                        <div>Inbound: <b>{inv.inbound_inventory ?? 0}</b></div>
                    </div>
                );
                return (
                    <Tooltip title={tooltipContent} placement="top" color="#1e1e2d">
                        <span className="fw-bold cursor-pointer"
                              style={{ borderBottom: "1px dotted #adadad" }}>
                            {val}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            title: "Days Until Stockout",
            key: "days_on_hand",
            dataIndex: "days_on_hand",
            sorter: (a, b) => a.days_on_hand - b.days_on_hand,
        },
        {
            title: "Inventory Health",
            key: "inventory_status",
            dataIndex: "inventory_status",
            sorter: (a, b) =>
                (a.inventory_status || "").localeCompare(b.inventory_status || ""),
            render: (status) => {
                let badgeClass = "badge-light-primary";
                const lower = status?.toLowerCase() || "";
                if (lower.includes("in stock") || lower === "healthy")        badgeClass = "badge-light-success";
                else if (lower.includes("low") || lower.includes("alert"))    badgeClass = "badge-light-warning";
                else if (lower.includes("out") || lower.includes("overstock")) badgeClass = "badge-light-danger";
                return (
                    <span className={`badge ${badgeClass} fw-bold px-4 py-3`}>{status || 'N/A'}</span>
                );
            },
        },
        {
            title: "Status",
            key: "status",
            dataIndex: "status",
            render: status => (
                <span className={`badge ${status === 0 ? 'badge-light-success' : 'badge-light-danger'}`}>
                    {status === 0 ? 'Active' : 'Inactive'}
                </span>
            ),
        },
        {
            title: "Details",
            key: "details",
            render: (_, record) => (
                <span
                    className="cursor-pointer fw-bold text-hover-danger"
                    style={{ color: '#D8372E' }}
                    onClick={() => openDetails(record)}
                >
                    See Details
                </span>
            ),
        },
    ], [selectedCurrencySign, page, pageSize, openDetails]);

    // ─── Column visibility (same logic as original) ───────────────────────────
    const [visibleColumns, setVisibleColumns] = useState(
        () => allColumns.map(col => col.key).filter(key => key !== 'tags')
    );

    const columns = useMemo(
        () => allColumns.filter(col => visibleColumns.includes(col.key)),
        [allColumns, visibleColumns]
    );

    const toggleableColumns = useMemo(
        () => allColumns.filter(
            col => col.key !== 'index' && col.key !== 'productDetails' && col.key !== 'details'
        ),
        [allColumns]
    );

    const handleColumnToggle = useCallback(checkedValues => {
        setVisibleColumns(['index', 'productDetails', 'details', ...checkedValues]);
    }, []);

    useEffect(() => {
        if (!isMobile && onColumnsReady) {
            onColumnsReady({ toggleableColumns, visibleColumns, handleColumnToggle });
        }
    }, [toggleableColumns, visibleColumns, handleColumnToggle, isMobile, onColumnsReady]);

    useEffect(() => {
        if (isMobile && onColumnsReady) onColumnsReady(null);
    }, [isMobile, onColumnsReady]);

    // ─── Mobile cards ─────────────────────────────────────────────────────────
    const renderMobileCards = () => (
        <div className="d-flex flex-column gap-3">
            {pagedProducts.map((item, index) => (
                <div
                    key={item.sku || index}
                    className="card border-0 shadow-sm p-4 rounded-4 cursor-pointer"
                    style={{ background: '#fff', border: '1px solid #f0f0f0' }}
                    onClick={() => openDetails(item)}
                >
                    <div className="d-flex align-items-center mb-4">
                        <img src={item.image_url || fakeProImage} alt="pro" className="rounded-2 me-3"
                             style={{ width: '50px', height: '50px', objectFit: 'contain', border: '1px solid #f5f5f5' }} />
                        <div className="overflow-hidden">
                            <div className="fw-bolder text-gray-800 text-truncate fs-6"
                                 style={{ maxWidth: '220px' }}>
                                {item.product_title || 'N/A'}
                            </div>
                            <div className="text-muted fs-8">
                                <b>SKU:</b> {item.sku} <span className="mx-1">|</span> <b>ASIN:</b> {item.asin}
                            </div>
                        </div>
                    </div>
                    <div className="row g-4 border-top pt-4">
                        <div className="col-4">
                            <div className="fs-9 text-uppercase text-muted fw-bold mb-1">Units</div>
                            <div className="fw-bolder text-dark">{item.total_units}</div>
                        </div>
                        <div className="col-4">
                            <div className="fs-9 text-uppercase text-muted fw-bold mb-1">Sales</div>
                            <div className="fw-bolder text-dark">{getAmount(item.sales, selectedCurrencySign)}</div>
                        </div>
                        <div className="col-4">
                            <div className="fs-9 text-uppercase text-muted fw-bold mb-1">Net Profit</div>
                            <div className={`fw-bolder ${item.net_profit >= 0 ? 'text-success' : 'text-danger'}`}>
                                {getAmount(item.net_profit, selectedCurrencySign)}
                            </div>
                        </div>
                        <div className="col-4">
                            <div className="fs-9 text-uppercase text-muted fw-bold mb-1">Refunds</div>
                            <Tooltip
                                trigger="click"
                                color="#1e1e2d"
                                title={
                                    <div className="p-2" style={{ minWidth: '180px' }}>
                                        <div className="fw-bold mb-2 border-bottom pb-1" style={{ color: '#ff4d4f' }}>
                                            Refund Reasons
                                        </div>
                                        {(item?.refund_breakup?.reasons || []).length > 0
                                            ? item.refund_breakup.reasons.map((r, idx) => (
                                                <div key={idx} className="d-flex justify-content-between gap-3 mb-1 fs-8">
                                                    <span className="text-capitalize">
                                                        {r.reason.toLowerCase().replace(/_/g, ' ')}:
                                                    </span>
                                                    <b>{r.count}</b>
                                                </div>
                                            ))
                                            : <div className="fs-8 text-muted">No reasons provided</div>}
                                    </div>
                                }
                            >
                                <div className="fw-bolder text-danger"
                                     style={{ borderBottom: "1px dotted #ff4d4f", display: 'inline-block' }}>
                                    {item.refund_unit}
                                </div>
                            </Tooltip>
                        </div>
                        <div className="col-4">
                            <div className="fs-9 text-uppercase text-muted fw-bold mb-1">Ref. Cost</div>
                            <div className="fw-bolder text-muted">
                                {getAmount(item.refund_cost, selectedCurrencySign)}
                            </div>
                        </div>
                        <div className="col-4">
                            <div className="fs-9 text-uppercase text-muted fw-bold mb-1">Ad Spend</div>
                            <div className="fw-bolder text-primary">
                                {getAmount(item.ad_spend, selectedCurrencySign)}
                            </div>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    );

    // ─── Summary row (server totals — unchanged) ──────────────────────────────
    const renderSummary = () => {
        if (!totals) return null;

        const summaryDataMap = {
            total_units:   totals.total_units,
            refund_unit:   totals.refund_unit,
            refund_cost:   getAmount(totals.refund_cost, selectedCurrencySign),
            sales:         <span className="text-dark">{getAmount(totals.sales, selectedCurrencySign)}</span>,
            ad_sales:      getAmount(totals.ad_sales, selectedCurrencySign),
            ad_spend:      getAmount(totals.ad_spend, selectedCurrencySign),
            cogs:          totals.cogs?.toFixed(2),
            organic_sales: getAmount(totals.organic_sales, selectedCurrencySign),
            tax:           getAmount(totals.tax, selectedCurrencySign),
            fees:          getAmount(totals.fees, selectedCurrencySign),
            gross_profit:  getAmount(totals.gross_profit, selectedCurrencySign),
            net_profit:    (
                <span className={totals.net_profit >= 0 ? 'text-success' : 'text-danger'}>
                    {getAmount(totals.net_profit, selectedCurrencySign)}
                </span>
            ),
            inventory: totals.inventory,
        };

        return (
            <Table.Summary fixed>
                <Table.Summary.Row className="bg-light fw-bolder">
                    {columns.map((col, index) => (
                        <Table.Summary.Cell key={col.key} index={index}>
                            {col.key === 'index'
                                ? ''
                                : col.key === 'productDetails'
                                    ? 'TOTALS'
                                    : (summaryDataMap[col.key] || '')}
                        </Table.Summary.Cell>
                    ))}
                </Table.Summary.Row>
            </Table.Summary>
        );
    };

    // ─── Render ───────────────────────────────────────────────────────────────
    return (
        <div className="products-container">
            <div className="row gy-5 g-xl-5">
                <div className="col-xxl-12">
                    {isMobile ? (
                        <div className="mobile-view-container px-1">
                            {pagedProducts.length > 0
                                ? renderMobileCards()
                                : <Empty description="No Products Found" />}
                        </div>
                    ) : (
                        <div className="table-responsive shadow-sm rounded">
                            <Table
                                scroll={{ x: "max-content" }}
                                columns={columns}
                                loading={loading}
                                dataSource={pagedProducts}
                                rowKey={record => record.sku || record.asin}
                                pagination={false}
                                summary={renderSummary}
                                locale={{ emptyText: <Empty description="No Products Found" /> }}
                                components={{
                                    header: {
                                        row: props => <tr {...props} className="fw-bolder text-dark bg-cus" />,
                                    },
                                }}
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Pagination — client-side against filteredProducts.length */}
            <div className="card border-0 shadow-sm mt-5">
                <div className="card-body py-4">
                    <Pagination
                        current={page}
                        pageSize={pageSize}
                        total={filteredProducts.length}
                        showSizeChanger
                        onChange={(newPage, newPageSize) => {
                            setPage(newPage);
                            setPageSize(newPageSize);
                        }}
                        showTotal={(total, range) =>
                            isMobile
                                ? `Total: ${total}`
                                : `${range[0]}-${range[1]} of ${total} products`}
                        responsive
                        size={isMobile ? "small" : "default"}
                        simple={isMobile}
                    />
                </div>
            </div>

            <ProductDetailsModal
                open={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                data={selectedRow}
                currencySign={selectedCurrencySign}
            />
        </div>
    );
};

export default React.memo(Products);